import type { FC, ReactNode } from 'react'

import { Phone } from 'lucide-react'
import { Container } from '@/components/generals/container'
import { SectionHeading } from '@/components/generals/section-heading'
import { Reveal } from '@/components/motion/reveal'
import { QuoteForm } from '@/components/widgets/quote-form'
import { siteConfig } from '@/constants/site'

export const quoteSteps = [
    "A price range arrives by text.",
    "You choose the visit window.",
    "Before any work begins, your technician confirms the exact price on site."
]

export const QuoteSteps: FC = (): ReactNode => {
    return (
        <Reveal as='ol' selector=':scope > li' className='space-y-0'>
            {quoteSteps.map((step, index) => (
                <li key={step} className='flex gap-5 border-t border-ink/10 py-5'>
                    <span className='flex size-9 shrink-0 items-center justify-center rounded-full bg-ink font-mono text-[12px] text-cream'>
                        {index + 1}
                    </span>

                    <span className='pt-1.5 text-[16px] leading-relaxed text-ink'>{step}</span>
                </li>
            ))}
        </Reveal>
    )
}

export const EmergencyNote: FC = (): ReactNode => {
    return (
        <a
            href={siteConfig.phoneHref}
            className='group mt-8 flex items-center gap-4 rounded-[22px] bg-ink p-5 text-cream transition-colors hover:bg-graphite'
        >
            <span className='flex size-11 shrink-0 items-center justify-center rounded-full bg-ember text-white'>
                <Phone className='size-4' aria-hidden='true' />
            </span>

            <span className='text-[15px] leading-snug'>
                <span className='block font-medium'>Need help this minute? Skip the form.</span>
                <span className='block text-cream/60'>Call <span className='font-mono'>{siteConfig.phone}</span>. We pick up until {siteConfig.emergencyUntil}.</span>
            </span>
        </a>
    )
}

export const QuoteSection: FC = (): ReactNode => {
    return (
        <section id='quote' className='bg-sand/60 py-24 md:py-32'>
            <Container className='grid gap-12 lg:grid-cols-12 lg:gap-10'>
                <div className='lg:col-span-5'>
                    <div className='lg:sticky lg:top-32'>
                        <SectionHeading
                            layout='stack'
                            eyebrow="Get a price"
                            title="Describe the door. Get your price by text."
                            titleClassName='max-w-[15ch]'
                            description="Only the questions your job calls for, nothing extra. Someone on our team replies in about 30 minutes during business hours."
                        />

                        <div className='mt-10'>
                            <QuoteSteps />
                        </div>

                        <EmergencyNote />
                    </div>
                </div>

                <Reveal className='lg:col-span-7' y={60}>
                    <QuoteForm entry='home' />
                </Reveal>
            </Container>
        </section>
    )
}
